import {
  Component,
  input,
  signal,
  computed,
  ElementRef,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, Router } from '@angular/router';
import { AuthService } from '../../../core/service/auth.service';
import { PaymentService } from '../../../core/service/payment.service';
import { CommunicationService } from '../../service/api/communication.service';
import { NotificationDialogPayload } from '../../models/notification-dialog.models';
import { ProfileMenuItem } from '../../models/profile-menu.models';
import { ToastService } from '../toast/toast.service';
import { NotificationDialog } from '../notification-dialog/notification-dialog';
import { CONTACT_URL } from '../../constants/app.constants';

@Component({
  selector: 'app-profile-menu',
  imports: [RouterLink, NotificationDialog],
  templateUrl: './profile-menu.html',
  styleUrl: './profile-menu.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onClickOutside($event)',
  },
})
/**
 * Represents a profile menu component that manages user profile interactions.
 */
export class ProfileMenu {
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);
  private paymentService = inject(PaymentService);
  private communicationService = inject(CommunicationService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  public readonly userName = computed(() => this.authService.currentUser()?.username ?? 'Guest');
  public readonly userEmail = computed(() => this.authService.currentUser()?.email ?? '');
  public readonly isAdmin = input(false);
  public isProfileMenuOpen = signal(false);
  public isNotificationOpen = signal(false);

  /**
   * Reactive first name derived from `userName`.
   */
  public readonly firstName = computed(() => {
    const name = this.userName()?.trim() ?? '';
    const parts = name ? name.split(/\s+/) : [];
    return parts[0] ?? '';
  });

  /**
   * Reactive last name derived from `userName` (uses last part).
   */
  public readonly lastName = computed(() => {
    const name = this.userName()?.trim() ?? '';
    const parts = name ? name.split(/\s+/) : [];
    return parts.length > 1 ? parts[parts.length - 1] : '';
  });

  /**
   * Reactive initials derived from `userName` (two letters: first & last).
   * Examples: 'Xhoi Kabashi' -> 'XK', 'Lea Ypi' -> 'LY', 'Single' -> 'SI'.
   */
  public readonly initials = computed(() => {
    const name = this.userName()?.trim() ?? '';
    if (!name) return '';
    const parts = name.split(/\s+/);
    const first = parts[0]?.[0]?.toUpperCase() ?? '';
    const last =
      parts.length > 1
        ? (parts[parts.length - 1][0]?.toUpperCase() ?? '')
        : (parts[0]?.[1]?.toUpperCase() ?? '');
    return first + last;
  });

  public readonly menuItems = computed((): ProfileMenuItem[] => [
    // Admin actions
    {
      type: 'button',
      label: 'Notifica generale',
      iconPath:
        'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      action: () => this.openNotification(),
      adminOnly: true,
    },
    {
      type: 'button',
      label: 'Gestione conservatori',
      iconPath:
        'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      action: () => this.openConservatoryManagement(),
      adminOnly: true,
    },
    {
      type: 'link',
      label: 'Assistenza',
      route: this.authService.requiresContractSignature()
        ? CONTACT_URL
        : '/communications/helpdesk',
      target: this.authService.requiresContractSignature() ? '_blank' : undefined,
      iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      nonAdminOnly: true,
    },
    {
      type: 'button',
      label: 'Gestisci pagamenti',
      iconPath:
        'M3 10h18M7 15h.01M11 15h2M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z',
      action: () => this.openPaymentPortal(),
      nonAdminOnly: true,
    },
    { type: 'separator' },
    {
      type: 'link',
      label: 'Condizioni Generali',
      route: '/terms-and-conditions',
      target: '_blank',
      iconPath:
        'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
    {
      type: 'link',
      label: 'Informativa Privacy',
      route: '/privacy-policy',
      target: '_blank',
      iconPath:
        'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    },
    {
      type: 'link',
      label: 'Atto di Nomina',
      route: '/lettera-di-nomina',
      target: '_blank',
      iconPath:
        'M8 7V3m8 4V3M5 11h14M5 7h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2zm3 8h8',
    },
    // Logout button
    {
      type: 'button',
      label: 'Sign out',
      iconPath:
        'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
      action: () => this.logout(),
      isLogout: true,
    },
  ]);

  public readonly visibleItems = computed(() =>
    this.menuItems().filter((item) => {
      if (item.adminOnly && !this.isAdmin()) return false;
      if (item.nonAdminOnly && this.isAdmin()) return false;
      return true;
    }),
  );

  toggleProfileMenu(): void {
    this.isProfileMenuOpen.update((v) => !v);
  }

  onClickOutside(event: Event): void {
    if (this.isProfileMenuOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeMenus();
    }
  }

  closeMenus(): void {
    this.isProfileMenuOpen.set(false);
  }

  openNotification(): void {
    this.isNotificationOpen.set(true);
  }

  closeNotification(): void {
    this.isNotificationOpen.set(false);
  }

  openConservatoryManagement(): void {
    this.router.navigate(['/conservatori']);
  }

  openPaymentPortal(): void {
    if (this.isAdmin()) {
      return;
    }

    this.paymentService
      .openBillingPortal()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.toastService.info('Reindirizzamento al portale pagamenti in corso...');
          window.open(response.portal_url, '_blank', 'noopener,noreferrer');
        },
        error: () => {
          console.error('Error initializing payment portal');
        },
      });
  }

  sendGlobalNotification(payload: NotificationDialogPayload): void {
    // Show success immediately as the request might take some time
    this.toastService.success('Richiesta di invio notifica generale presa in carico');
    this.isNotificationOpen.set(false);

    this.communicationService
      .sendCommonNotification({
        message_subject: payload.subject,
        message_text: payload.message,
        ...(payload.validFrom && { valid_from: payload.validFrom }),
        ...(payload.validTo && { valid_to: payload.validTo }),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          console.error('Error sending general notification:', err);
          this.toastService.error("Errore durante l'invio della notifica generale");
        },
      });
  }

  logout(): void {
    this.authService.logout();
  }

  handleMenuItemClick(item: ProfileMenuItem): void {
    if (item.action) {
      item.action();
      this.closeMenus();
    }
  }
}
